import { NO_IMAGE } from "@/_constant";
import BestDealsBanner from "@/assets/images/plan-previews/best-deals-banner.png";
import ExclusivePropertiesBanner from "@/assets/images/plan-previews/exclusive-properties-banner.png";
import TopPropertiesBanner from "@/assets/images/plan-previews/top-properties-banner.png";
import { fetchListingPlans } from "@/apis";
import { useLanguageContext } from "@/hooks";
import { renderHeading, renderPrice, renderSubtitle, renderTitle } from "@/utility";
import { useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import "./ListingAddonPlanPopup.css";

export default function ListingAddonPlanPopup({
    title,
    listingDetails,
    listingPlans = [],
    defaultSelectedPlan = {},
    isShowNonGreatDealPlan = true,
    onClose,
    onSelectPlan,
    onSubmitSelectedPlan,
    isSkipAndPost = false
}) {
    const { language } = useLanguageContext();

    const [fetchedPlans, setFetchedPlans] = useState(listingPlans && listingPlans.length > 0 ? listingPlans : []);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        if (listingPlans && listingPlans.length > 0) {
            setFetchedPlans(listingPlans);
        } else {
            setIsLoading(true);
            const categoryId = listingDetails?.categoryId || listingDetails?.category_id || sessionStorage.getItem("selectedDrop") || "2";
            const requestPayload = {
                category_id: categoryId,
                is_active: true,
                // planType: listingDetails?.planType || "OFFER"
                planType: "ADD_ON_DEALER"
            };

            fetchListingPlans(requestPayload)
                .then((res) => {
                    const plansData = res?.data?.response || res?.response || [];
                    setFetchedPlans(plansData);
                })
                .catch((err) => {
                    console.error("Error fetching seller plans:", err);
                })
                .finally(() => {
                    setIsLoading(false);
                });
        }
    }, [listingPlans, listingDetails]);

    const plans = useMemo(() => {
        return fetchedPlans && fetchedPlans.length > 0 ? fetchedPlans : [];
    }, [fetchedPlans]);

    const getPlanId = (plan, idx = 0) => String(plan?.id || plan?._id || plan?.planId || plan?.tenure || `plan-${idx}`);

    const [selectedPlan, setSelectedPlan] = useState(null);
    const [expandedPlanId, setExpandedPlanId] = useState("");
    const [selectedOptions, setSelectedOptions] = useState([]);
    const initializedRef = useRef(false);

    const totalPrice = useMemo(() => {
        if (!selectedOptions || selectedOptions.length === 0) return 0;
        return selectedOptions.reduce((sum, opt) => sum + (Number(opt?.price) || 0), 0);
    }, [selectedOptions]);

    // Helper to generate options per plan
    const getPlanOptions = (plan, planIndex = 0) => {
        if (!plan) return [];
        if (plan?.options && plan.options.length > 0) {
            return plan.options.map((opt, idx) => ({
                ...opt,
                id: String(opt.id || opt._id || `${getPlanId(plan, planIndex)}-opt-${idx}`)
            }));
        }

        const basePrice = plan?.price != null ? plan.price : 200;
        const baseTenure = plan?.tenure != null ? plan.tenure : 10;
        const planId = getPlanId(plan, planIndex) || "plan";

        const options = [];

        if (plan?.greatDealBannerPrice != null && Number(plan.greatDealBannerPrice) > 0) {
            options.push({
                id: `${planId}-best-deals`,
                price: Number(plan.greatDealBannerPrice),
                tenure: baseTenure,
                label: language("label.show_your_post_in_best_deal_banners") || "Show your post in best deal banners",
                image: BestDealsBanner,
                isGreatDeal: false,
                optionKey: "withGreatDealBanner"
            });
        }

        if (plan?.isTopProperty) {
            options.push({
                id: `${planId}-top-properties`,
                price: Number(plan?.topPropertyPrice || 0),
                tenure: baseTenure,
                label: language("label.show_your_post_in_top_property_listing") || "Show your post in top property listing",
                image: TopPropertiesBanner,
                isGreatDeal: true,
                optionKey: "isTopProperty"
            });
        }

        if (plan?.isExclusive) {
            options.push({
                id: `${planId}-exclusive-properties`,
                price: Number(plan?.exclusivePrice || 0),
                tenure: baseTenure,
                label: language("label.show_your_post_in_exclusive_property_listing") || "Show your post in exclusive property listing",
                image: ExclusivePropertiesBanner,
                isGreatDeal: "exclusive",
                optionKey: "isExclusive"
            });
        }

        return options;
    };

    useEffect(() => {
        if (plans.length > 0 && !initializedRef.current) {
            initializedRef.current = true;
            const initialPlan = defaultSelectedPlan?.id
                ? plans.find((p) => String(getPlanId(p)) === String(defaultSelectedPlan.id)) || plans[0]
                : plans[0];
            setSelectedPlan(initialPlan);
            setExpandedPlanId(getPlanId(initialPlan));
            const options = getPlanOptions(initialPlan);
            setSelectedOptions(options.length > 0 ? [options[0]] : []);
        }
    }, [plans]);

    const handleAccordionToggle = (plan, index = 0) => {
        const id = getPlanId(plan, index);
        setExpandedPlanId(id);
        setSelectedPlan(plan);
        const options = getPlanOptions(plan, index);
        setSelectedOptions(options.length > 0 ? [options[0]] : []);
        if (onSelectPlan) {
            onSelectPlan(plan);
        }
    };

    const handleSelectOption = (e, plan, planIndex, option) => {
        e.stopPropagation();
        const id = getPlanId(plan, planIndex);
        if (String(expandedPlanId) !== String(id) || String(getPlanId(selectedPlan, planIndex)) !== String(id)) {
            setExpandedPlanId(id);
            setSelectedPlan(plan);
            setSelectedOptions([option]);
            if (onSelectPlan) {
                onSelectPlan(plan);
            }
            return;
        }

        setSelectedOptions((prevSelected) => {
            const exists = prevSelected.some((opt) => String(opt.id) === String(option.id));
            if (exists) {
                return prevSelected.filter((opt) => String(opt.id) !== String(option.id));
            } else {
                return [...prevSelected, option];
            }
        });
    };

    const handleConfirm = () => {
        if (onSubmitSelectedPlan && selectedPlan && selectedOptions.length > 0) {
            const withGreatDealBanner = selectedOptions.some(
                (opt) => opt.optionKey === "withGreatDealBanner" || opt.type === "withGreatDealBanner" || opt.id?.includes("best-deals") || opt.withGreatDealBanner === true
            );
            const isTopProperty = selectedOptions.some(
                (opt) => opt.optionKey === "isTopProperty" || opt.type === "isTopProperty" || opt.id?.includes("top-properties") || opt.isTopProperty === true
            );
            const isExclusive = selectedOptions.some(
                (opt) => opt.optionKey === "isExclusive" || opt.type === "isExclusive" || opt.id?.includes("exclusive-properties") || opt.isExclusive === true
            );
            const isGreatDeal = selectedOptions.some((opt) => opt.isGreatDeal === true);
            const flags = { withGreatDealBanner, isTopProperty, isExclusive };

            onSubmitSelectedPlan(selectedPlan, withGreatDealBanner, selectedOptions, flags);
        }
    };

    const handleSkip = () => {
        if (onClose) {
            onClose();
        }
    };

    const renderListingAddress = (item) => {
        console.log('item', item);
        if (!item) return "";

        const isUuid = (str) =>
            typeof str === "string" &&
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/.test(str.trim());

        const houseNoStreet =
            item?.houseNoStreet ||
            item?.house_no_street ||
            item?.houseNo ||
            (typeof item?.address === "object" ? item?.address?.houseNoStreet || item?.address?.house_no_street : "") ||
            item?.listPreviewSellerInfoDTO?.houseNoStreet ||
            item?.listPreviewSellerInfoDTO?.house_no_street ||
            item?.sellerInfo?.houseNoStreet ||
            item?.addressDetails?.houseNoStreet ||
            "";

        const villageCity =
            item?.villageCity ||
            item?.city ||
            (typeof item?.address === "object" ? item?.address?.villageCity || item?.address?.city : "") ||
            item?.listPreviewSellerInfoDTO?.villageCity ||
            item?.sellerInfo?.villageCity ||
            "";

        const tehsilName =
            item?.tehsilName ||
            item?.tehsil ||
            (typeof item?.address === "object" ? item?.address?.tehsilName || item?.address?.tehsil : "") ||
            item?.listPreviewSellerInfoDTO?.tehsilName ||
            "";

        const districtName =
            item?.districtName ||
            item?.district ||
            item?.district_name ||
            (typeof item?.address === "object" ? item?.address?.districtName || item?.address?.district : "") ||
            item?.listPreviewSellerInfoDTO?.districtName ||
            "";

        const stateName =
            item?.stateName ||
            item?.state ||
            (typeof item?.address === "object" ? item?.address?.stateName || item?.address?.state : "") ||
            item?.listPreviewSellerInfoDTO?.stateName ||
            "";

        const parts = [houseNoStreet, villageCity, tehsilName, districtName, stateName]
            .filter((p) => Boolean(p) && typeof p === "string" && p.trim().length > 0 && !isUuid(p));

        if (parts.length > 0) {
            return parts.join(", ");
        }

        if (typeof item?.address === "string" && item.address.trim() && !isUuid(item.address)) {
            return item.address.trim();
        }

        if (typeof item?.addressText === "string" && item.addressText.trim() && !isUuid(item.addressText)) {
            return item.addressText.trim();
        }

        if (typeof item?.formattedAddress === "string" && item.formattedAddress.trim() && !isUuid(item.formattedAddress)) {
            return item.formattedAddress.trim();
        }

        const subtitle = renderSubtitle(item);
        if (subtitle && typeof subtitle === "string" && !isUuid(subtitle)) {
            return subtitle;
        }

        return "";
    };

    const modalContent = (
        <div className="addon-plan-modal-overlay">
            <div className="addon-plan-modal-dialog">
                {/* Header */}
                <div className="addon-plan-modal-header">
                    <h3 className="addon-plan-modal-title">
                        {title || language("label.choose_a_plan") || "Choose a plan"}
                    </h3>
                    <button type="button" className="addon-plan-modal-close-btn" onClick={onClose} aria-label="Close">
                        <i className="bi bi-x-lg"></i>
                    </button>
                </div>

                {/* Body */}
                <div className="addon-plan-modal-body">
                    {/* Listing Card */}
                    <div className="addon-plan-listing-card">
                        <img
                            src={listingDetails?.thumbnailUrl || listingDetails?.image || NO_IMAGE}
                            alt="listing"
                            className="addon-plan-listing-img"
                        />
                        <div className="addon-plan-listing-details">
                            <span className="addon-plan-listing-price">
                                {renderPrice(listingDetails) || "₹ 40.50 Lac - 68 Lac"}
                            </span>
                            <span className="addon-plan-listing-title">
                                {renderTitle(listingDetails) || "Property Name Here"}
                            </span>
                            <div className="addon-plan-listing-divider" />
                            <span className="addon-plan-listing-category">
                                {renderHeading(listingDetails) || "Villa House, Residential Plot"}
                            </span>
                            <span className="addon-plan-listing-location">
                                <i className="bi bi-geo-alt-fill" style={{ color: "#0f172a" }}></i>
                                {renderListingAddress(listingDetails) ||
                                    "RR Nagar, Bangalore, Karnataka"}
                            </span>
                        </div>
                    </div>

                    {/* Loading State or Accordion Plan List */}
                    {isLoading ? (
                        <div className="text-center py-5 my-3">
                            <div className="spinner-border" role="status" style={{ color: "#8b279c" }}>
                                <span className="visually-hidden">Loading...</span>
                            </div>
                            <p className="mt-2 text-muted fw-semibold">
                                {language("label.loading_plans") || "Loading plans..."}
                            </p>
                        </div>
                    ) : plans && plans.length > 0 ? (
                        plans.map((plan, index) => {
                            const planId = getPlanId(plan, index);
                            const isExpanded = String(expandedPlanId) === String(planId);
                            const isPlanActive = selectedPlan && String(getPlanId(selectedPlan, index)) === String(planId);
                            const options = getPlanOptions(plan, index);
                            const planDisplayName = plan.name || plan.title || plan.planName || (plan.tenure ? `PLAN ${plan.tenure}` : `PLAN ${index + 1}`);

                            return (
                                <div
                                    key={planId || index}
                                    className={`addon-plan-accordion ${isExpanded || isPlanActive ? "active" : "inactive"}`}
                                >
                                    {/* Accordion Header */}
                                    <div
                                        className="addon-plan-accordion-header"
                                        onClick={() => handleAccordionToggle(plan, index)}
                                    >
                                        <div className="addon-plan-accordion-header-left">
                                            <div
                                                className={`addon-plan-radio-circle ${isPlanActive ? "active" : "inactive"
                                                    }`}
                                            >
                                                {isPlanActive && <div className="addon-plan-radio-dot" />}
                                            </div>
                                            <span className="addon-plan-accordion-title">{planDisplayName}</span>
                                        </div>
                                        <i
                                            className={`bi ${isExpanded ? "bi-chevron-up" : "bi-chevron-down"
                                                } addon-plan-accordion-chevron`}
                                        />
                                    </div>

                                    {/* Accordion Content */}
                                    {isExpanded && (
                                        <div className="addon-plan-accordion-body">
                                            <div className="addon-plan-cards-grid">
                                                {options.map((opt) => {
                                                    const isOptionSelected =
                                                        isPlanActive && selectedOptions.some((item) => String(item.id) === String(opt.id));

                                                    return (
                                                        <div
                                                            key={opt.id}
                                                            className={`addon-plan-pkg-card ${isOptionSelected ? "selected" : "unselected"
                                                                }`}
                                                            onClick={(e) => handleSelectOption(e, plan, index, opt)}
                                                        >
                                                            <div className="addon-plan-pkg-header">
                                                                <div
                                                                    className={`addon-plan-checkbox ${isOptionSelected ? "checked" : "unchecked"
                                                                        }`}
                                                                >
                                                                    {isOptionSelected && <i className="bi bi-check-lg" />}
                                                                </div>
                                                                <span className="addon-plan-pkg-price-text">
                                                                    ₹{opt.price} for {opt.tenure} days
                                                                </span>
                                                            </div>

                                                            <div className="addon-plan-pkg-label">{opt.label}</div>

                                                            <div className="addon-plan-pkg-img-box">
                                                                <img
                                                                    src={opt.image}
                                                                    alt={opt.label}
                                                                    className="addon-plan-pkg-img"
                                                                />
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })
                    ) : (
                        <div className="text-center py-5 my-3 text-muted fw-semibold fs-5">
                            <i className="bi bi-info-circle me-2"></i>
                            {language("label.no_plan_found") || "No plans found"}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="addon-plan-modal-footer">
                    <button type="button" className="addon-plan-skip-btn" onClick={handleSkip}>
                        {language("label.skip_and_continue_to_post") || "Skip & continue to post"}
                    </button>
                    {plans && plans.length > 0 && (
                        <button
                            type="button"
                            className="addon-plan-pay-btn"
                            onClick={handleConfirm}
                            disabled={selectedOptions.length === 0}
                            style={{
                                opacity: selectedOptions.length === 0 ? 0.6 : 1,
                                cursor: selectedOptions.length === 0 ? "not-allowed" : "pointer"
                            }}
                        >
                            {language("label.pay") || "Pay"} ₹{totalPrice} & Post
                            <i className="bi bi-chevron-right ms-1"></i>
                        </button>
                    )}
                </div>
            </div>
        </div>
    );

    const portalTarget = typeof document !== "undefined"
        ? (document.querySelector(".dialog-box-container") || document.body)
        : null;

    return portalTarget ? ReactDOM.createPortal(modalContent, portalTarget) : modalContent;
}